import React, { useEffect, useState } from "react";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";
import { Alert, Badge, Button, Card, Col, Form, Row } from "react-bootstrap";
import { Link } from "react-router-dom";
import axiosInstance from "../services/axiosInterceptor";
import { useLoading } from '../contexts/LoadingContext';
import ThaiDateInput from '../components/ThaiDateInput';
import { toThaiDateLong } from '../utils/thaiDate';

const API = process.env.REACT_APP_API;

// การตั้งค่าของฝ่ายวัดผล เก็บใน tb_assessment_setting — key ใหม่เพิ่มที่
// server/models/assessmentSettingModel.js แล้วเพิ่มส่วนแสดงผลในหน้านี้

// วันถัดจากวันสิ้นสุดการส่งเกรด = วันที่พอร์ทัลผู้ปกครองเริ่มขึ้นผลจริง
const nextDay = (iso) => {
  const [y, m, d] = iso.split('-').map(Number);
  const t = new Date(y, m - 1, d + 1);
  const p = (n) => String(n).padStart(2, '0');
  return `${t.getFullYear()}-${p(t.getMonth() + 1)}-${p(t.getDate())}`;
};

// บันทึกค่าหนึ่งตัว — value null = กลับไปใช้ค่าตั้งต้น
const useSaveSetting = (yearId, onSaved) => {
  const { setLoading } = useLoading();
  const [message, setMessage] = useState(null);
  const save = async (key, semester, value) => {
    setLoading(true);
    setMessage(null);
    try {
      const { data } = await axiosInstance.put(`${API}/assessment/settings`, {
        key, year_id: yearId, semester, value: value || null,
      });
      onSaved(data);
      setMessage({ variant: 'success', text: 'บันทึกแล้ว' });
    } catch (err) {
      setMessage({ variant: 'danger', text: err.response?.data?.message || 'บันทึกไม่สำเร็จ' });
    } finally {
      setLoading(false);
    }
  };
  return { save, message };
};

// วันสิ้นสุดการส่งเกรดของภาคหนึ่ง — ก่อนพ้นวันนี้ พอร์ทัลนักเรียน/ผู้ปกครองแสดงวิชาที่คะแนน
// ยังไม่ครบเป็น "รอผล" แทน ร (ธง ร ถูกตั้งเองระหว่างภาคทุกครั้งที่ครูยังกรอกไม่ครบช่อง)
function GradeDeadlineCard({ yearId, item, onSaved }) {
  const [value, setValue] = useState(item.custom ? item.value : '');
  const { save, message } = useSaveSetting(yearId, onSaved);

  useEffect(() => { setValue(item.custom ? item.value : ''); }, [item]);

  return (
    <Card className="card-one h-100">
      <Card.Header className="d-flex align-items-center gap-2">
        <Card.Title as="h6" className="mb-0">ภาคเรียนที่ {item.semester}</Card.Title>
        {item.released
          ? <Badge bg="success" className="ms-auto">ผู้ปกครองเห็นผลจริงแล้ว</Badge>
          : <Badge bg="secondary" className="ms-auto">ผู้ปกครองเห็น "รอผล"</Badge>}
      </Card.Header>
      <Card.Body>
        <div className="mb-3">
          <div className="text-muted fs-sm">วันสิ้นสุดการส่งเกรดที่ใช้อยู่</div>
          <div className="fs-5 fw-semibold">{toThaiDateLong(item.value)}</div>
          <div className="fs-sm text-muted">
            {item.custom ? 'ฝ่ายวัดผลกำหนด' : 'ค่าตั้งต้น (ภาค 1: 15 ต.ค. / ภาค 2: 15 มี.ค.)'}
            {' · '}ผู้ปกครองเห็นผลจริงตั้งแต่ {toThaiDateLong(nextDay(item.value))}
          </div>
          {item.custom && item.updated_by_name && (
            <div className="fs-xs text-muted mt-1">ตั้งโดย {item.updated_by_name}</div>
          )}
        </div>

        <Form.Label className="fs-sm mb-1">กำหนดวันสิ้นสุดการส่งเกรด</Form.Label>
        <div className="d-flex flex-wrap gap-2 align-items-start">
          <div style={{ maxWidth: 200 }}>
            <ThaiDateInput value={value} onChange={(v) => setValue(v)} />
          </div>
          <Button variant="primary" disabled={!value || (item.custom && value === item.value)}
            onClick={() => save('grade_deadline', item.semester, value)}>
            บันทึก
          </Button>
          {item.custom && (
            <Button variant="outline-secondary" onClick={() => save('grade_deadline', item.semester, null)}>
              ใช้ค่าตั้งต้น ({toThaiDateLong(item.default_value, { short: true })})
            </Button>
          )}
        </div>
        {message && <Alert variant={message.variant} className="py-2 mt-3 mb-0 fs-sm">{message.text}</Alert>}
      </Card.Body>
    </Card>
  );
}

export default function AssessmentSettings() {
  const { setLoading } = useLoading();
  const [, setSkin] = useState(localStorage.getItem('skin-mode') ? 'dark' : '');
  const [yearList, setYearList] = useState([]);
  const [yearId, setYearId] = useState(null);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    axiosInstance.get(`${API}/getStudyYear`).then((res) => {
      const years = res.data || [];
      setYearList(years);
      if (years.length) setYearId(years[0].year_id);
    }).catch(() => setError('โหลดปีการศึกษาไม่สำเร็จ'));
  }, []);

  useEffect(() => {
    if (!yearId) return;
    setLoading(true);
    setError(null);
    axiosInstance.get(`${API}/assessment/settings/${yearId}`)
      .then((res) => setData(res.data))
      .catch((err) => setError(err.response?.data?.message || 'โหลดข้อมูลไม่สำเร็จ'))
      .finally(() => setLoading(false));
  }, [yearId]); // eslint-disable-line react-hooks/exhaustive-deps

  const gradeDeadline = data?.settings?.grade_deadline;

  return (
    <React.Fragment>
      <Header onSkin={setSkin} />
      <div className="main main-app p-3 p-lg-4">
        <div className="d-sm-flex align-items-center justify-content-between mb-4">
          <div>
            <ol className="breadcrumb fs-sm mb-1">
              <li className="breadcrumb-item"><Link to="#">วัดผลและประเมินผล</Link></li>
              <li className="breadcrumb-item active">ตั้งค่างานวัดผล</li>
            </ol>
            <h4 className="main-title mb-0">ตั้งค่างานวัดผล</h4>
          </div>
          <Form.Select className="mt-2 mt-sm-0" style={{ maxWidth: 220 }}
            value={yearId || ''} onChange={(e) => setYearId(Number(e.target.value))}>
            {yearList.map((y) => (
              <option key={y.year_id} value={y.year_id}>ปีการศึกษา {y.year_year}</option>
            ))}
          </Form.Select>
        </div>

        {error && <Alert variant="danger">{error}</Alert>}

        {gradeDeadline && (
          <section className="mb-4">
            <h6 className="mb-1">{gradeDeadline.label}</h6>
            <p className="text-muted fs-sm mb-3">
              ระหว่างภาคเรียน ระบบขึ้น ร ให้นักเรียนที่ครูยังกรอกคะแนนไม่ครบทุกช่องโดยอัตโนมัติ
              พอร์ทัลนักเรียน/ผู้ปกครองจึงแสดงวิชาเหล่านี้เป็น <b>"รอผล"</b> จนพ้นวันสิ้นสุดการส่งเกรด
              ตั้งแต่วันถัดไปจะแสดงผลจริง — วิชาที่ยังไม่ครบจะขึ้น <b>ร</b>.
              วิชาที่ครูกรอกครบแล้วแสดงเกรดได้ทันทีไม่ต้องรอวันนี้
            </p>
            <Row className="g-3">
              {gradeDeadline.items.map((item) => (
                <Col md={6} key={item.semester}>
                  <GradeDeadlineCard yearId={data.year_id} item={item} onSaved={setData} />
                </Col>
              ))}
            </Row>
          </section>
        )}
      </div>
      <Footer />
    </React.Fragment>
  );
}
